import axios from '@/utils/axios';
import type {
  AiMonitorConversation,
  AiMonitorPersistedMessage,
  AiMonitorResponse,
  AskAiMonitorInput,
} from '../types';

export const AI_MONITOR_CONVERSATIONS_LIMIT = 25;
export const AI_MONITOR_MESSAGES_LIMIT = 50;

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
};

type ConversationsPayload =
  | AiMonitorConversation[]
  | {
      conversations?: AiMonitorConversation[];
      data?: AiMonitorConversation[];
    };

type MessagesPayload =
  | AiMonitorPersistedMessage[]
  | {
      messages?: AiMonitorPersistedMessage[];
      data?: AiMonitorPersistedMessage[];
    };

const unwrapData = <T>(payload: unknown): T => {
  const response = payload as ApiEnvelope<T>;

  if (response?.data !== undefined) {
    return response.data;
  }

  return payload as T;
};

const unwrapList = <T>(
  payload: unknown,
  keys: Array<'conversations' | 'messages' | 'data'>
): T[] => {
  const unwrapped = unwrapData<unknown>(payload);

  if (Array.isArray(unwrapped)) return unwrapped as T[];

  if (unwrapped && typeof unwrapped === 'object') {
    const candidate = unwrapped as Record<string, unknown>;
    const list = keys
      .map((key) => candidate[key])
      .find((value) => Array.isArray(value));

    return Array.isArray(list) ? (list as T[]) : [];
  }

  return [];
};

export const buildAiMonitorPayload = (
  input: AskAiMonitorInput
): AskAiMonitorInput => {
  const payload: AskAiMonitorInput = {
    message: input.message,
  };

  if (input.conversation_id) {
    payload.conversation_id = input.conversation_id;
  }

  if (input.start_date && input.end_date) {
    payload.start_date = input.start_date;
    payload.end_date = input.end_date;
  } else if (input.range) {
    payload.range = input.range;
  }

  return payload;
};

export async function askAiMonitor(
  input: AskAiMonitorInput
): Promise<AiMonitorResponse> {
  const response = await axios.post<AiMonitorResponse>(
    '/me/dashboard/ai-monitor/chat',
    buildAiMonitorPayload(input)
  );

  return response.data;
}

export async function getAiMonitorConversations({
  limit = AI_MONITOR_CONVERSATIONS_LIMIT,
  offset = 0,
}: {
  limit?: number;
  offset?: number;
} = {}): Promise<AiMonitorConversation[]> {
  const { data } = await axios.get<ApiEnvelope<ConversationsPayload>>(
    '/me/dashboard/ai-monitor/conversations',
    {
      params: { limit, offset },
    }
  );

  return unwrapList<AiMonitorConversation>(data, ['conversations', 'data']);
}

export async function getAiMonitorConversationMessages(
  conversationId: number,
  {
    limit = AI_MONITOR_MESSAGES_LIMIT,
    offset = 0,
  }: {
    limit?: number;
    offset?: number;
  } = {}
): Promise<AiMonitorPersistedMessage[]> {
  const { data } = await axios.get<ApiEnvelope<MessagesPayload>>(
    `/me/dashboard/ai-monitor/conversations/${conversationId}/messages`,
    {
      params: { limit, offset },
    }
  );

  return unwrapList<AiMonitorPersistedMessage>(data, ['messages', 'data']);
}

export async function archiveAiMonitorConversation(
  conversationId: number
): Promise<void> {
  await axios.delete(
    `/me/dashboard/ai-monitor/conversations/${conversationId}`
  );
}
